"use client";
// Force rebuild to resolve "module factory is not available" error

import { CssBaseline, ThemeProvider, createTheme, PaletteMode } from "@mui/material";
import { ReactNode, useMemo, useState, useEffect } from "react";


export function OwnerThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<PaletteMode>("light");

  useEffect(() => {
    const isDark = document.documentElement.classList.contains("dark");
    setMode(isDark ? "dark" : "light");

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          const isDark = document.documentElement.classList.contains("dark");
          setMode(isDark ? "dark" : "light");
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => {
      observer.disconnect();
    };
  }, []);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: "#6366f1", 
            contrastText: "#ffffff",
          },
          secondary: {
            main: "#64748b",
          },
          text: {
            primary: mode === "light" ? "#0f172a" : "#f8fafc", 
            secondary: mode === "light" ? "#64748b" : "#94a3b8",
          },
          background: {
            default: mode === "light" ? "#f8fafc" : "#020617",
            paper: mode === "light" ? "#ffffff" : "#0f172a",
          },
          divider: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
          success: { main: "#10b981" },
          warning: { main: "#f59e0b" },
          error: { main: "#ef4444" },
        },
        shape: { borderRadius: 12 },
        typography: {
          fontFamily: "var(--tc-font-sans)",
          h1: { fontWeight: 800, letterSpacing: "-0.025em" },
          h2: { fontWeight: 800, letterSpacing: "-0.025em" },
          h3: { fontWeight: 700, letterSpacing: "-0.025em" },
          h4: { fontWeight: 700, letterSpacing: "-0.015em" },
          h5: { fontWeight: 700 },
          h6: { fontWeight: 700 },
          subtitle1: { fontWeight: 500 },
          body1: { fontSize: "var(--tc-text-md)", lineHeight: 1.6 },
          body2: { fontSize: "var(--tc-text-sm)", lineHeight: 1.57 },
          button: { fontWeight: 600, textTransform: "none" },
          caption: { color: "var(--tc-text-muted)", fontWeight: 500 },
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                backgroundColor: "var(--tc-bg)",
                color: "var(--tc-text)",
                transition: "background-color var(--tc-transition-standard), color var(--tc-transition-standard)",
              },
            },
          },
          MuiTypography: {
            styleOverrides: {
              root: {
                color: "inherit",
              },
              gutterBottom: {
                marginBottom: "0.75em",
              },
            },
          },
          MuiPaper: {
            defaultProps: {
              elevation: 0,
            },
            styleOverrides: {
              root: {
                backgroundColor: "var(--tc-surface)",
                backgroundImage: "none",
                borderRadius: "var(--tc-radius-lg)",
                border: "1px solid var(--tc-border)",
                transition: "background-color var(--tc-transition-standard), border-color var(--tc-transition-standard), box-shadow var(--tc-transition-standard)",
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: "var(--tc-radius-lg)",
                border: "1px solid var(--tc-border)",
                boxShadow: "var(--tc-shadow-sm)",
                transition: "all var(--tc-transition-standard)",
                "&:hover": {
                  boxShadow: "var(--tc-shadow-md)",
                },
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: "var(--tc-radius-md)",
                padding: "8px 16px",
                transition: "all var(--tc-transition-standard)",
              },
              contained: {
                boxShadow: "var(--tc-shadow-sm)",
                "&:hover": {
                  boxShadow: "var(--tc-shadow-md)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              },
              outlined: {
                borderWidth: "1.5px",
                borderColor: "var(--tc-border)",
                color: "var(--tc-text)",
                "&:hover": {
                  borderWidth: "1.5px",
                  borderColor: "var(--tc-primary-border)",
                  backgroundColor: "var(--tc-primary-soft)",
                },
              },
              text: {
                color: "var(--tc-text)",
                "&:hover": {
                  backgroundColor: "var(--tc-bg-muted)",
                },
              },
            },
          },
          MuiTextField: {
            defaultProps: {
              variant: "outlined",
              size: "small",
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                borderRadius: "var(--tc-radius-md)",
                backgroundColor: "var(--tc-bg-muted)",
                transition: "all var(--tc-transition-fast)",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--tc-border)",
                  transition: "border-color var(--tc-transition-fast)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--tc-primary-border)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderWidth: "2px",
                  borderColor: "var(--tc-primary)",
                },
                "&.Mui-focused": {
                  backgroundColor: "var(--tc-surface)",
                },
              },
              input: {
                color: "var(--tc-text)",
                "&::placeholder": {
                  color: "var(--tc-text-muted)",
                  opacity: 0.7,
                }
              }
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                fontWeight: 600,
                borderRadius: "var(--tc-radius-sm)",
                transition: "all var(--tc-transition-fast)",
              },
              sizeSmall: {
                fontSize: "0.75rem",
              },
            },
            variants: [
              {
                props: { variant: "filled", color: "success" },
                style: {
                  backgroundColor: "var(--tc-success-soft)",
                  color: "var(--tc-success)",
                  border: "1px solid var(--tc-success-border)",
                },
              },
            ],
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                borderBottom: "1px solid var(--tc-border-soft)",
                padding: "16px",
              },
              head: {
                fontWeight: 700,
                color: "var(--tc-text-muted)",
                backgroundColor: "var(--tc-bg-muted)",
                textTransform: "uppercase",
                fontSize: "0.75rem",
                letterSpacing: "0.05em",
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                backgroundColor: "var(--tc-surface-elevated)",
                color: "var(--tc-text)",
                borderRadius: "var(--tc-radius-md)",
                padding: "8px 12px",
                fontSize: "0.75rem",
                fontWeight: 600,
                border: "1px solid var(--tc-border)",
                boxShadow: "var(--tc-shadow-lg)",
              },
              arrow: {
                color: "var(--tc-surface-elevated)",
                "&:before": {
                  border: "1px solid var(--tc-border)",
                },
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                borderRadius: "var(--tc-radius-xl)",
                padding: "8px",
                backgroundImage: "none",
                backgroundColor: "var(--tc-surface)",
                boxShadow: "var(--tc-shadow-premium)",
              },
            },
          },
          MuiTableContainer: {
            styleOverrides: {
              root: {
                borderRadius: "var(--tc-radius-lg)",
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "var(--tc-border)",
                  borderRadius: "var(--tc-radius-full)",
                  border: "2px solid var(--tc-surface)",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "var(--tc-text-muted)",
                },
              },
            },
          },
          MuiTablePagination: {
            styleOverrides: {
              root: {
                borderTop: "1px solid var(--tc-border-soft)",
                color: "var(--tc-text-muted)",
                fontWeight: 600,
              },
              selectLabel: {
                fontWeight: 600,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              },
              displayedRows: {
                fontWeight: 700,
                fontSize: "0.75rem",
                color: "var(--tc-text)",
              },
            },
          },
          MuiDivider: {
            styleOverrides: {
              root: {
                borderColor: "var(--tc-divider)",
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
